import type React from 'react';
import { AlertTriangle, Check, CircleDashed, Loader2, RefreshCw, X } from 'lucide-react';
import type { AudioStatus, StatusCounts } from '@tekvoice/shared';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { STATUS_LABELS } from '@/lib/assets';
import { cn } from '@/lib/utils';

const VARIANTS: Record<AudioStatus, NonNullable<BadgeProps['variant']>> = {
  PENDING: 'secondary',
  GENERATING: 'secondary',
  FAILED: 'destructive',
  GENERATED: 'outline',
  APPROVED: 'success',
  STALE: 'warning',
};

const ICONS: Record<AudioStatus, React.ComponentType<{ className?: string }>> = {
  PENDING: CircleDashed,
  GENERATING: Loader2,
  FAILED: X,
  GENERATED: Check,
  APPROVED: Check,
  STALE: RefreshCw,
};

interface AudioStatusChipProps {
  status: AudioStatus;
  /**
   * C8 — a single-asset regeneration is in flight. It does NOT replace the status:
   * an approved clip stays approved (and keeps exporting) while a new take is produced,
   * so the chip reads "approved + regenerating".
   */
  regenerating?: boolean;
  /** Shown in a tooltip — the failure reason, or "text changed — regenerate". */
  hint?: string | null;
  className?: string;
}

/** SPEC §11 — the per-asset status chip (pending/generating/failed/generated/approved/stale). */
export function AudioStatusChip({
  status,
  regenerating = false,
  hint,
  className,
}: AudioStatusChipProps): React.JSX.Element {
  const Icon = ICONS[status];
  const chip = (
    <span className="inline-flex items-center gap-1">
      <Badge
        variant={VARIANTS[status]}
        className={cn('gap-1 whitespace-nowrap px-1.5 py-0 text-[11px] font-medium', className)}
        data-testid={`status-${status}`}
        data-status={status}
      >
        <Icon className={cn('h-3 w-3', status === 'GENERATING' && 'animate-spin')} aria-hidden />
        {STATUS_LABELS[status]}
      </Badge>
      {regenerating ? (
        <Badge
          variant="secondary"
          className="gap-1 whitespace-nowrap px-1.5 py-0 text-[11px] font-medium"
          data-testid="status-regenerating"
        >
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
          regenerating
        </Badge>
      ) : null}
    </span>
  );

  if (!hint) return chip;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} aria-label={`${STATUS_LABELS[status]}: ${hint}`}>
          {chip}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-72">{hint}</TooltipContent>
    </Tooltip>
  );
}

const COUNT_ORDER: AudioStatus[] = [
  'APPROVED',
  'GENERATED',
  'STALE',
  'FAILED',
  'GENERATING',
  'PENDING',
];

/** Compact per-status asset counts (batch list + batch header). */
export function StatusCountsRow({
  counts,
  className,
}: {
  counts: StatusCounts;
  className?: string;
}): React.JSX.Element {
  const visible = COUNT_ORDER.filter((status) => counts[status] > 0);
  if (visible.length === 0) {
    return <span className={cn('text-xs text-muted-foreground', className)}>no audio yet</span>;
  }
  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)}>
      {visible.map((status) => (
        <Badge
          key={status}
          variant={VARIANTS[status]}
          className="gap-1 px-1.5 py-0 text-[11px] font-medium"
          data-testid={`count-${status}`}
        >
          {status === 'FAILED' ? (
            <AlertTriangle className="h-3 w-3" aria-hidden />
          ) : null}
          {counts[status]} {STATUS_LABELS[status]}
        </Badge>
      ))}
    </div>
  );
}
